import { useCallback, useContext, useEffect, useState } from "react";
import fileDownload from "js-file-download";
import { useTranslation } from "react-i18next";
import { ARTWORK_TYPES, BEHAVES_LIKE } from "../../../constant/common.constant";
import TechpackArtworkDescription from "./TechpackArtworkDescription";
import TechpackArtworkImage from "./TechpackArtworkImage";
import TechpackService from "../../../service/TechpackService";
import ImageService from "../../../service/ImageService";
import TechpackCustomColor from "../TechpackCustomColor";
import {
  TechpackItemArtwork,
  ProductDefinitionDecoration,
  GLASS_MAGNIFIER_BACKGROUND_COLORS,
} from "../../../types";
import { Typography } from "@mui/material";
import { Box, FlexColBox } from "../../../common/components";
import { palette } from "../../../styles/theme";
import { AdminTechpackContext } from "../../../context/Admin/Techpack";

interface Props {
  techpackId: string;
  techpackItemId: string;
  techpackItemArtwork: TechpackItemArtwork;
  techpackItemArtworkIndex: number;
  techpackItemArtworkCount: number;
  decoration: TechpackItemArtwork["decoration"];
  decorationDefinition?: ProductDefinitionDecoration;
}

const TechpackArtwork = (props: Props) => {
  const {
    techpackId,
    techpackItemId,
    techpackItemArtwork,
    techpackItemArtworkIndex,
    techpackItemArtworkCount,
    decoration,
    decorationDefinition,
  } = props;

  const { t } = useTranslation();
  const { updateTechpackItemArtwork } = useContext(AdminTechpackContext);

  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [generatedPatternImageUrl, setGeneratedPatternImageUrl] = useState<
    string | undefined
  >();

  useEffect(() => {
    const backgroundColorName =
      decoration?.repeatingDetails?.backgroundColorName;
    if (!backgroundColorName || !decoration?.assetId) {
      setGeneratedPatternImageUrl(undefined);
      return;
    }

    const { repeatingDetails } = decoration;
    ImageService.getRepeatingPrintImage(
      decoration.assetId,
      decoration.assetVersionId,
      decoration.type,
      repeatingDetails.patternName,
      repeatingDetails.horizontalSpacingInches,
      repeatingDetails.verticalSpacingInches,
      repeatingDetails.maxImageWidthInches,
      repeatingDetails.maxImageHeightInches,
      repeatingDetails.horizontalOffsetInches,
      repeatingDetails.verticalOffsetInches,
      undefined,
      backgroundColorName
    )
      .then((data: any) => {
        setGeneratedPatternImageUrl(data?.url || data?.fullSizeUrl);
      })
      .catch(() => {
        setGeneratedPatternImageUrl(undefined);
      });
  }, [decoration]);

  const techpackItemArtworkId = techpackItemArtwork.id;
  const customColors = techpackItemArtwork.techpackitemartworkcustomcolors;
  const productionDecoration = techpackItemArtwork.productionDecoration;

  /**
   *
   */
  const productionBackgroundColorUpdate = useCallback(
    async (newBackgroundColor: string) => {
      const productionArtwork = {
        techpackId,
        techpackItemId,
        techpackItemArtworkId: techpackItemArtwork.id,
        background: { color: newBackgroundColor },
      };

      const prodUpdated =
        await TechpackService.updateProductionArtworkBackgroundColor(
          productionArtwork
        );
      return prodUpdated;
    },
    [techpackId, techpackItemId, techpackItemArtwork.id]
  );

  /**
   *
   */
  const previewBackgroundColorUpdate = useCallback(
    async (newBackgroundColor: string) => {
      const productionArtwork = {
        techpackId,
        techpackItemId,
        techpackItemArtworkId: techpackItemArtwork.id,
        background: { color: newBackgroundColor },
      };

      const prodUpdated =
        await TechpackService.updatePreviewArtworkBackgroundColor(
          productionArtwork
        );
      return prodUpdated;
    },
    [techpackId, techpackItemId, techpackItemArtwork.id]
  );

  /**
   *
   */
  const originalBackgroundColorUpdate = useCallback(
    async (newBackgroundColor: string) => {
      const productionArtwork = {
        techpackId,
        techpackItemId,
        techpackItemArtworkId: techpackItemArtwork.id,
        background: { color: newBackgroundColor },
      };

      const prodUpdated =
        await TechpackService.updateOriginalArtworkBackgroundColor(
          productionArtwork
        );
      return prodUpdated;
    },
    [techpackId, techpackItemId, techpackItemArtwork.id]
  );

  /**
   *
   */
  const createProductionAssetFromFile = useCallback(
    async (assetId: string, file: File) => {
      const assetType = ARTWORK_TYPES.TECHPACK_PRODUCTION;

      //Create the asset type
      const { assetVersionId, errorKey } = await ImageService.createAsset(
        assetId,
        assetType
      );

      if (errorKey) {
        throw new Error(errorKey);
      }

      const productionArtwork = {
        techpackId,
        techpackItemId,
        techpackItemArtworkId,
        assetId,
        assetVersionId,
        assetType,
        includeImagesUrl: true,
      };

      const prodUpdated =
        await TechpackService.updateProductionArtwork(productionArtwork);

      return prodUpdated.data.data.production_decoration;
    },
    [techpackId, techpackItemId, techpackItemArtworkId]
  );

  /**
   *
   */
  const uploadFileAndCreateProductionAsset = useCallback(
    async ({ file, onSuccess }: { file: File; onSuccess: () => void }) => {
      if (!file) {
        const errorKey = "noFileGiven";
        setErrorCode(errorKey);
        return;
      }

      const { assetId } = await ImageService.uploadAssetAsAdmin(file);
      try {
        const newProductionDecoration = await createProductionAssetFromFile(
          assetId,
          file
        );

        const updatedTechpackItemArtwork = {
          ...techpackItemArtwork,
          productionDecoration: newProductionDecoration,
        };
        const hasWhite =
          techpackItemArtwork.decoration.asset?.selectedColors?.some(
            (color) => color.chartColor.hex === "#FFFFFF"
          );
        if (hasWhite) {
          await productionBackgroundColorUpdate(
            GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT_GREY
          );
          updatedTechpackItemArtwork.productionDecorationBackground = {
            color: GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT_GREY,
          };
        }
        updateTechpackItemArtwork(techpackItemId, updatedTechpackItemArtwork);
        setErrorCode(null);
      } catch (e) {
        updateTechpackItemArtwork(techpackItemId, {
          ...techpackItemArtwork,
          productionDecoration: null,
        });
        setErrorCode(e instanceof Error ? e.message : "Unknown error");
      } finally {
      }

      onSuccess();
    },
    [
      createProductionAssetFromFile,
      updateTechpackItemArtwork,
      techpackItemId,
      techpackItemArtwork,
      productionBackgroundColorUpdate,
    ]
  );

  const removeProductionAsset = useCallback(async () => {
    const productionArtwork = {
      techpackItemArtworkId: techpackItemArtwork.id,
    };

    const prodUpdated =
      await TechpackService.removeProductionArtwork(productionArtwork);
    updateTechpackItemArtwork(techpackItemId, {
      ...techpackItemArtwork,
      productionDecoration: null,
    });
    return prodUpdated;
  }, [techpackItemArtwork, updateTechpackItemArtwork, techpackItemId]);

  /**
   *
   */
  const downloadUrl = useCallback(
    (imageUrl: string | undefined, assetName: string | undefined) => {
      if (!imageUrl || !assetName) {
        return;
      }

      ImageService.getS3ImageContent(imageUrl)
        .then((res) => {
          fileDownload(res.data, assetName, res.headers["content-type"]);
        })
        .catch((e) => {
          console.error("Unable to download the image from the link");
        });
    },
    []
  );
  const originalBackgroundColor =
    techpackItemArtwork?.originalDecorationBackground?.color ??
    GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT_GREY;
  const previewBackgroundColor =
    techpackItemArtwork?.previewDecorationBackground?.color ??
    GLASS_MAGNIFIER_BACKGROUND_COLORS.LIGHT_GREY;
  const productionBackgroundColor =
    techpackItemArtwork?.productionDecorationBackground?.color;

  return (
    <FlexColBox
      sx={{
        p: "24px",
        backgroundColor: palette.white,
        borderRadius: "8px",
        border: `1px solid ${palette.grey.lightGrey}`,
      }}
    >
      <Typography variant="textLargeBold" mb={3}>
        {`${t(`techpack.artwork`)} (${techpackItemArtworkIndex + 1}/${techpackItemArtworkCount}) - ${t(`specs.decoration_types.${decorationDefinition?.id}.title`)}`}
      </Typography>
      <Box display="flex" flexDirection="row" fullWidth gap={1.5}>
        <Box maxWidth="240px" width="240px" fullWidth>
          <TechpackArtworkDescription
            decoration={decoration}
            decorationDefinition={decorationDefinition}
            productionDecoration={productionDecoration}
            techpackId={techpackId}
            techpackItemId={techpackItemId}
            techpackItemArtwork={techpackItemArtwork}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "48px",
            maxWidth: "calc(100% - 240px)",
            "&>div": {
              width: "33.33%",
            },
          }}
          fullWidth
        >
          <TechpackArtworkImage
            imageUrl={decoration.assetOriginal?.fullSizeUrl || ""}
            title={t("techpack.source_file")}
            errorCode={errorCode}
            backgroundColor={
              originalBackgroundColor as GLASS_MAGNIFIER_BACKGROUND_COLORS
            }
            allowDownload={true}
            onDownloadUrl={() =>
              downloadUrl(
                decoration.assetOriginal?.originalUrl,
                decoration.assetOriginal?.assetName
              )
            }
            onBackgroundColorChange={originalBackgroundColorUpdate}
          />

          {(decorationDefinition?.behavesLike === BEHAVES_LIKE.WOVEN_TAG_TYPE ||
            decorationDefinition?.behavesLike ===
              BEHAVES_LIKE.LEATHER_TAG_TYPE ||
            decorationDefinition?.behavesLike ===
              BEHAVES_LIKE.EMBROIDERY_PATCH_TYPE) &&
          decoration.assetGeneratedTag ? (
            <TechpackArtworkImage
              imageUrl={decoration.assetGeneratedTag.url}
              title={t("techpack.preview_file")}
              backgroundColor={
                previewBackgroundColor as GLASS_MAGNIFIER_BACKGROUND_COLORS
              }
              allowOpenInNewTab={true}
              noBackgroundColor={true}
              onBackgroundColorChange={previewBackgroundColorUpdate}
              errorCode={errorCode}
            />
          ) : (
            <TechpackArtworkImage
              imageUrl={
                (decoration.repeatingDetails?.backgroundColorName &&
                  generatedPatternImageUrl) ||
                decoration.asset?.fullSizeUrl ||
                ""
              }
              title={t("techpack.preview_file")}
              backgroundColor={
                techpackItemArtwork?.previewDecorationBackground
                  ?.color as GLASS_MAGNIFIER_BACKGROUND_COLORS
              }
              allowOpenInNewTab={true}
              onBackgroundColorChange={previewBackgroundColorUpdate}
              errorCode={errorCode}
            />
          )}

          <TechpackArtworkImage
            imageUrl={productionDecoration?.asset?.fullSizeUrl}
            title={t("techpack.production_file")}
            errorCode={errorCode}
            backgroundColor={
              productionBackgroundColor as GLASS_MAGNIFIER_BACKGROUND_COLORS
            }
            allowDownload={productionDecoration ? true : false}
            allowReplace={true}
            allowRemove={true}
            allowLightGrey={true}
            onRemove={removeProductionAsset}
            onUpload={uploadFileAndCreateProductionAsset}
            onDownloadUrl={() =>
              downloadUrl(
                productionDecoration?.assetOriginal?.originalUrl,
                productionDecoration?.assetOriginal?.assetName
              )
            }
            onBackgroundColorChange={productionBackgroundColorUpdate}
          />
        </Box>
      </Box>
      {customColors && customColors.length > 0 && (
        <Box
          display={"flex"}
          flexDirection={"column"}
          mt={"12px"}
          maxWidth={"240px"}
        >
          <TechpackCustomColor
            customColors={customColors}
            hideCustomColorLabel
            hideUsedOnTechpackPositions
          />
        </Box>
      )}
    </FlexColBox>
  );
};

export default TechpackArtwork;
